import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Visibility as EyeIcon,
  Note as NoteIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getDatabase, ref, get } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { TestResult } from '../types/testTypes';
import AmslerGridCardCanvas from './AmslerGridCardCanvas';
import { useFirebaseService } from '../contexts/FirebaseServiceContext';
import TestResultDialog from './TestResultDialog';
import { downloadTestResultImage, shareTestResult, printTestResult } from '../utils/testResultUtils';
import { AmslerGridData } from '../types/AmslerGridData';

interface FullTestResultDialogProps {
  open: boolean;
  onClose: () => void;
  testId: string | null;
}

const FullTestResultDialog: React.FC<FullTestResultDialogProps> = ({ 
  open, 
  onClose, 
  testId 
}) => {
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const { currentUser } = useAuth();
  const { firebaseService } = useFirebaseService();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchTestResult = async () => {
      if (!testId || !currentUser || !open) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch from Firebase Realtime Database
        const db = getDatabase();
        const testRef = ref(db, `users/${currentUser.uid}/grids/${testId}`);
        
        const snapshot = await get(testRef);
        
        if (!snapshot.exists()) {
          setError('Test result not found');
          setLoading(false);
          return;
        }
        
        const data = snapshot.val();
        
        // Verify that the test belongs to the current user
        if (data.userId !== currentUser.uid) {
          setError('You do not have permission to view this test result');
          setLoading(false);
          return;
        }
        
        setTestResult({
          id: testId,
          userId: data.userId,
          timestamp: data.timestamp,
          eye: data.eyeTested,
          markedPoints: data.dots || data.markedPoints || [],
          abnormalities: data.abnormalities || [],
          notes: data.notes || '',
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching test result:', err);
        setError('Failed to load test result. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchTestResult();
  }, [testId, currentUser, open]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'PPpp'); // Format: Mar 15, 2023, 3:45 PM
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Invalid date';
    }
  };

  const handleOpenFullSizeView = () => {
    setImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
  };

  const handleShare = () => {
    if (!testResult) return;
    
    const testData = {
      timestamp: testResult.timestamp,
      eyeTested: testResult.eye,
      dots: testResult.markedPoints,
      abnormalities: testResult.abnormalities,
      notes: testResult.notes
    };
    
    shareTestResult(testData);
  };

  const handleDownload = () => {
    if (!testResult) return;
    
    const testData = {
      timestamp: testResult.timestamp,
      eyeTested: testResult.eye,
      dots: testResult.markedPoints,
      abnormalities: testResult.abnormalities,
      notes: testResult.notes
    };
    
    downloadTestResultImage(testData);
  };

  const handlePrint = () => {
    if (!testResult) return;
    
    const testData = {
      timestamp: testResult.timestamp,
      eyeTested: testResult.eye,
      dots: testResult.markedPoints,
      abnormalities: testResult.abnormalities,
      notes: testResult.notes
    };
    
    printTestResult(testData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          maxHeight: '100%',
          height: '100%',
          m: fullScreen ? 0 : 2,
          mt: fullScreen ? 0 : 7,
          borderRadius: fullScreen ? 0 : 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'rgba(18, 18, 18, 0.95)',
          border: '1px solid #333333',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.5)'
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.85)'
        }
      }}
    >
      <AppBar position="sticky" elevation={0} sx={{ 
        backgroundColor: 'rgba(0, 188, 212, 0.95)',
        color: '#000000',
        zIndex: 1100,
        flexShrink: 0,
        borderBottom: '1px solid #008ba3'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ mr: 2 }}
          >
            <CloseIcon />
          </IconButton>
          {testResult && (
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="div" sx={{ color: '#000000', fontWeight: 500 }}>
                {testResult.eye === 'left' ? 'Left' : 'Right'} Eye Test
              </Typography>
              <Box display="flex" alignItems="center">
                <CalendarIcon fontSize="small" sx={{ mr: 0.5, color: '#000000', opacity: 0.7 }} />
                <Typography variant="subtitle2" component="div" sx={{ color: '#000000', opacity: 0.7 }}>
                  {formatDate(testResult.timestamp)}
                </Typography>
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ 
        p: 3, 
        overflow: 'auto',
        flex: 1,
        bgcolor: 'rgba(18, 18, 18, 0.95)',
        color: '#ffffff',
        backgroundImage: 'none'
      }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : testResult ? (
          <Box>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>
                  Test Visualization
                </Typography>
                
                <Card 
                  variant="outlined" 
                  sx={{ 
                    mb: 3, 
                    height: 400, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    bgcolor: 'rgba(30, 30, 30, 0.95)',
                    borderColor: '#333333',
                    '&:hover': {
                      boxShadow: '0px 0px 10px rgba(0, 188, 212, 0.3)',
                      transform: 'scale(1.01)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                  onClick={handleOpenFullSizeView}
                >
                  {testResult.markedPoints && testResult.markedPoints.length > 0 ? (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      padding: 2
                    }}>
                      <AmslerGridCardCanvas 
                        dots={testResult.markedPoints.map(point => ({
                          x: point.x,
                          y: point.y,
                          timestamp: testResult.timestamp
                        }))}
                        curves={[]}
                        width={300}
                        height={300}
                      />
                    </Box>
                  ) : (
                    <Typography color="text.secondary" align="center">
                      No abnormalities were marked on the grid
                    </Typography>
                  )}
                </Card>
                
                <Box display="flex" gap={1} justifyContent="center">
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{
                      color: '#00bcd4',
                      borderColor: '#00bcd4',
                      '&:hover': {
                        borderColor: '#00a0b4',
                        backgroundColor: 'rgba(0, 188, 212, 0.08)'
                      }
                    }}
                  >
                    Print
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{
                      color: '#00bcd4',
                      borderColor: '#00bcd4',
                      '&:hover': {
                        borderColor: '#00a0b4',
                        backgroundColor: 'rgba(0, 188, 212, 0.08)'
                      }
                    }}
                  >
                    Download
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<ShareIcon />}
                    onClick={handleShare}
                    sx={{
                      color: '#00bcd4',
                      borderColor: '#00bcd4',
                      '&:hover': {
                        borderColor: '#00a0b4',
                        backgroundColor: 'rgba(0, 188, 212, 0.08)'
                      }
                    }}
                  >
                    Share
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>
                  Test Details
                </Typography>
                
                <Card variant="outlined" sx={{ 
                  mb: 3,
                  bgcolor: 'rgba(30, 30, 30, 0.95)',
                  borderColor: '#333333',
                  color: '#ffffff'
                }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <EyeIcon sx={{ mr: 1, color: '#00bcd4' }} />
                      <Typography variant="body1" sx={{ color: '#ffffff' }}>
                        <strong>Eye Tested:</strong> {testResult.eye === 'left' ? 'Left Eye' : 'Right Eye'}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2, borderColor: '#333333' }} />
                    
                    <Box mb={2}>
                      <Typography variant="body1" gutterBottom sx={{ color: '#ffffff' }}>
                        <strong>Abnormalities Detected:</strong>
                      </Typography>
                      
                      {testResult.abnormalities && testResult.abnormalities.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {testResult.abnormalities.map((abnormality, index) => (
                            <Chip 
                              key={index} 
                              label={abnormality} 
                              color="error" 
                              size="small"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography color="text.secondary">
                          No abnormalities detected
                        </Typography>
                      )}
                    </Box>
                    
                    <Divider sx={{ my: 2, borderColor: '#333333' }} />
                    
                    <Box>
                      <Typography variant="body1" gutterBottom display="flex" alignItems="center" sx={{ color: '#ffffff' }}>
                        <NoteIcon sx={{ mr: 1, color: '#00bcd4' }} />
                        <strong>Notes:</strong>
                      </Typography>
                      
                      {testResult.notes ? (
                        <Typography variant="body2" sx={{ mt: 1, px: 1, color: '#ffffff' }}>
                          {testResult.notes}
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">
                          No notes for this test
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
                
                <Alert severity="info" sx={{
                  bgcolor: 'rgba(0, 188, 212, 0.12)',
                  color: '#e0e0e0',
                  '& .MuiAlert-icon': {
                    color: '#00bcd4'
                  }
                }}>
                  If you notice changes in your vision, please consult with an eye care professional.
                </Alert>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Typography color="text.primary">No test data available</Typography>
        )}
      </DialogContent>

      {/* Test Result Dialog for full-size view */}
      {testResult && (
        <TestResultDialog
          open={imageDialogOpen}
          onClose={handleCloseImageDialog}
          testData={{
            timestamp: testResult.timestamp,
            eyeTested: testResult.eye,
            dots: testResult.markedPoints ? testResult.markedPoints.map(point => {
              // Create a new dot object with required fields
              const dot: any = {
                x: point.x,
                y: point.y,
                timestamp: testResult.timestamp
              };
              
              // Add optional fields if they exist
              if ('intensity' in point && point.intensity !== undefined) {
                dot.intensity = point.intensity;
              }
              
              if ('distortionRadius' in point && point.distortionRadius !== undefined) {
                dot.distortionRadius = point.distortionRadius;
              }
              
              return dot;
            }) : [],
            abnormalities: testResult.abnormalities,
            notes: testResult.notes
          }}
          onShare={handleShare}
          onDownload={handleDownload}
          onPrint={handlePrint}
        />
      )}
    </Dialog>
  );
};

export default FullTestResultDialog; 